import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Modal } from './ui/Modal';
import OsScroll from './ui/OsScroll';
import { useVariablesStore } from '../store/variablesStore';
import { useVaultStore } from '../store/vaultStore';
import './Vault.css';

/**
 * "Import from environments": lists every variable flagged `secret` across
 * environments + workspace/global variable sets, and turns the checked ones
 * into authHeader vault items (headerName = variable key, headerValue =
 * value, sourceEnv = where it came from). Opened from the vault titlebar's
 * download action ('vault-import-env').
 */

const rowId = (scope, key, i) => `${scope} ${key} ${i}`;

export default function VaultImportEnvModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const environments = useVariablesStore((s) => s.environments);
  const workspaceVariables = useVariablesStore((s) => s.workspaceVariables);
  const globalVariables = useVariablesStore((s) => s.globalVariables);

  const [checked, setChecked] = useState(() => new Set());
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  const rows = useMemo(() => {
    const out = [];
    const collect = (scopeName, variables) => {
      (Array.isArray(variables) ? variables : []).forEach((v, i) => {
        if (v?.secret !== true) return;
        const key = (v.key || '').trim();
        if (!key) return;
        out.push({
          id: rowId(scopeName, key, i),
          envName: scopeName,
          key,
          value: v.value ?? '',
        });
      });
    };
    for (const env of Array.isArray(environments) ? environments : []) {
      collect(env?.name || t('common.unknown'), env?.variables);
    }
    collect(t('vault.importScopeWorkspace'), workspaceVariables);
    collect(t('vault.importScopeGlobal'), globalVariables);
    return out;
  }, [environments, workspaceVariables, globalVariables, t]);

  useEffect(() => {
    if (!isOpen) return;
    setChecked(new Set());
    setImporting(false);
    setError(null);
  }, [isOpen]);

  const toggle = (id) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allChecked = rows.length > 0 && checked.size === rows.length;
  const toggleAll = () =>
    setChecked(allChecked ? new Set() : new Set(rows.map((r) => r.id)));

  const handleImport = async () => {
    const selected = rows.filter((r) => checked.has(r.id));
    if (selected.length === 0 || importing) return;
    setImporting(true);
    setError(null);
    try {
      // Sequential puts (the Rust side serializes writes anyway); one refresh
      // at the end instead of per-item.
      for (const row of selected) {
        await invoke('vault_put_item', {
          item: {
            type: 'authHeader',
            name: row.key,
            data: {
              headerName: row.key,
              headerValue: row.value,
              sourceEnv: row.envName,
            },
          },
        });
      }
      await useVaultStore.getState().refresh();
      onClose();
    } catch (e) {
      setError(String(e?.message || e));
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('vault.importTitle')}
      subtitle={t('vault.importSubtitle')}
      size="lg"
    >
      <div className="space-y-4">
        {rows.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-zinc-300">{t('vault.importEmpty')}</p>
        ) : (
          <>
            <label className="ext-checkbox-label">
              <input
                type="checkbox"
                className="checkbox-brand"
                checked={allChecked}
                onChange={toggleAll}
              />
              {t('vault.importSelectAll')}
            </label>
            <OsScroll className="vault-import-list">
              <div>
                {rows.map((row) => (
                  <label key={row.id} className="vault-import-row">
                    <input
                      type="checkbox"
                      className="checkbox-brand"
                      checked={checked.has(row.id)}
                      onChange={() => toggle(row.id)}
                    />
                    <span className="vault-import-env">{row.envName}</span>
                    <span className="vault-import-key">{row.key}</span>
                    <span className="vault-import-value">••••••</span>
                  </label>
                ))}
              </div>
            </OsScroll>
          </>
        )}

        {error && <div className="vault-modal-error">{error}</div>}

        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={checked.size === 0 || importing}
            onClick={handleImport}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {importing ? t('vault.importing') : t('vault.importButton')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
